import React from 'react';
import { Download, Share, SquarePlus, X } from 'lucide-react';
import { C } from '@parsina/framework/clinical/src/constants/theme';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import {
  dismissInstallPrompt,
  getInstallState,
  promptInstall,
  subscribeInstallPrompt,
} from '../../lib/pwa';

/**
 * The install banner — the browser-mode invitation to become an app.
 *
 * Two shapes behind one card, because the platforms genuinely differ:
 *
 *   Android/Chromium  the captured `beforeinstallprompt` is replayed, so
 *                     «نصب» opens the REAL system dialog — one tap.
 *   iOS               there is no API; «نصب» flips the card into the
 *                     three-step share-sheet walkthrough instead.
 *
 * Visibility is entirely `getInstallState()`'s call (standalone, installed,
 * cooldown, something-to-offer) so the component renders nothing everywhere
 * that matters: installed launches, desktop, dismissed-recently sessions.
 * Mounted only in the mobile shell.
 */
const InstallPrompt = () => {
  const { t } = useTranslation();
  const [state, setState] = React.useState(getInstallState);
  const [showIosSteps, setShowIosSteps] = React.useState(false);

  React.useEffect(
    () => subscribeInstallPrompt(() => setState(getInstallState())),
    [],
  );

  if (!state.visible) return null;

  const handleInstall = async () => {
    if (state.promptAvailable) {
      await promptInstall();
    } else {
      setShowIosSteps(true);
    }
  };

  const iosSteps = [
    { Icon: Share, text: t('common.pwa.iosStep1') },
    { Icon: SquarePlus, text: t('common.pwa.iosStep2') },
    { Icon: Download, text: t('common.pwa.iosStep3') },
  ];

  return (
    <div
      data-testid="pwa-install-prompt"
      role="dialog"
      aria-label={t('common.pwa.installTitle')}
      style={{
        position: 'fixed',
        insetInline: 12,
        // Above the bottom nav when there is one, above the home indicator
        // when there is not. `--app-bottom-nav` is published by
        // MobileBottomNav while it is mounted and removed when it unmounts, so
        // the same expression is right on a tab-bar screen and on a full-screen
        // flow. Without it the card was laid out from the window's bottom edge
        // and covered the tab bar it was floating over.
        bottom: 'calc(12px + env(safe-area-inset-bottom, 0px) + var(--app-bottom-nav, 0px))',
        zIndex: 90,
        background: '#fff',
        border: `1px solid ${C.border}`,
        borderRadius: 16,
        boxShadow: '0 12px 40px rgba(0,0,0,0.16), 0 2px 10px rgba(0,0,0,0.08)',
        padding: 14,
        maxWidth: 440,
        marginInline: 'auto',
      }}
    >
      {!showIosSteps ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 46, height: 46, borderRadius: 12, flexShrink: 0,
            background: `linear-gradient(160deg, ${C.navyDark}, ${C.navyLight})`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Download size={22} color="#fff" aria-hidden />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '0.84375rem', fontWeight: 800, color: C.textPrimary }}>
              {t('common.pwa.installTitle')}
            </div>
            <div style={{ fontSize: '0.71875rem', color: C.textSecondary, lineHeight: 1.7, marginTop: 2 }}>
              {t('common.pwa.installBody')}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
            <button
              data-testid="pwa-install-accept"
              onClick={handleInstall}
              style={{
                padding: '8px 16px', borderRadius: 10, border: 'none',
                background: C.navy, color: '#fff', fontWeight: 700,
                fontSize: '0.78125rem', fontFamily: 'inherit', cursor: 'pointer',
              }}
            >
              {t('common.pwa.install')}
            </button>
            <button
              data-testid="pwa-install-dismiss"
              onClick={dismissInstallPrompt}
              style={{
                padding: '4px 8px', borderRadius: 8, border: 'none',
                background: 'transparent', color: C.textMuted,
                fontSize: '0.71875rem', fontFamily: 'inherit', cursor: 'pointer',
              }}
            >
              {t('common.pwa.later')}
            </button>
          </div>
        </div>
      ) : (
        <div data-testid="pwa-ios-steps">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <div style={{ fontSize: '0.84375rem', fontWeight: 800, color: C.textPrimary }}>
              {t('common.pwa.iosTitle')}
            </div>
            <button
              onClick={dismissInstallPrompt}
              aria-label={t('common.actions.close')}
              style={{
                width: 28, height: 28, borderRadius: 8, border: 'none',
                background: C.bg, color: C.textMuted, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <X size={15} aria-hidden />
            </button>
          </div>
          <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {iosSteps.map(({ Icon, text }, i) => (
              <li key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{
                  width: 30, height: 30, borderRadius: 9, flexShrink: 0,
                  background: `linear-gradient(160deg, ${C.navyDark}, ${C.navyLight})`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Icon size={15} color="#fff" aria-hidden />
                </span>
                <span style={{ fontSize: '0.78125rem', color: C.textSecondary, lineHeight: 1.7 }}>{text}</span>
              </li>
            ))}
          </ol>
          <button
            data-testid="pwa-ios-got-it"
            onClick={dismissInstallPrompt}
            style={{
              marginTop: 12, width: '100%', padding: '9px 0', borderRadius: 10,
              border: 'none', background: C.navy, color: '#fff', fontWeight: 700,
              fontSize: '0.78125rem', fontFamily: 'inherit', cursor: 'pointer',
            }}
          >
            {t('common.pwa.gotIt')}
          </button>
        </div>
      )}
    </div>
  );
};

export default InstallPrompt;
